import { useState, useRef, useCallback } from "react";
import Papa from "papaparse";
import api from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Upload, Download, FileSpreadsheet, AlertCircle, Check, X, Loader2, Undo2 } from "lucide-react";
import { toast } from "sonner";

// Generic bulk-importer. Specify `type`: "clients" | "products".
const PRESETS = {
  clients: {
    title: "Import Clients",
    description: "Upload a CSV file to add or update multiple clients at once.",
    endpoint: "/clients/bulk-import",
    requiredColumns: ["first_name", "email"],
    optionalColumns: ["last_name", "phone", "birthday", "notes", "referral_source", "tags"],
    templateName: "clients-import-template.csv",
    templateSample: [
      { first_name: "Jane", last_name: "Doe", email: "jane@example.com", phone: "5551234567", birthday: "1990-05-12", notes: "Prefers color services", referral_source: "instagram", tags: "vip, color" },
      { first_name: "Alex", last_name: "Kim", email: "alex.kim@example.com", phone: "5559876543", birthday: "", notes: "", referral_source: "walk-in", tags: "" },
    ],
  },
  products: {
    title: "Import Products",
    description: "Upload a CSV file to add or update inventory items.",
    endpoint: "/products/bulk-import",
    requiredColumns: ["name", "price"],
    optionalColumns: ["category", "cost", "sku", "stock", "low_stock_threshold", "reorder_point", "reorder_qty", "vendor"],
    templateName: "products-import-template.csv",
    templateSample: [
      { name: "Olaplex No. 3", category: "Haircare", price: 32, cost: 18.50, sku: "OLA-003", stock: 12, low_stock_threshold: 4, reorder_point: 8, reorder_qty: 24, vendor: "Olaplex" },
      { name: "Kerastase Elixir Ultime", category: "Haircare", price: 55, cost: 30, sku: "KER-ELX", stock: 6, low_stock_threshold: 3, reorder_point: 5, reorder_qty: 12, vendor: "L'Oreal" },
    ],
  },
};

export default function BulkImportDialog({ open, onOpenChange, type, onDone }) {
  const preset = PRESETS[type];
  const fileRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [fileName, setFileName] = useState("");
  const [parsedRows, setParsedRows] = useState([]);
  const [rowErrors, setRowErrors] = useState([]); // [{row, error}]
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // {created, updated, skipped, errors, total, batch_id}
  const [undoing, setUndoing] = useState(false);
  const [undone, setUndone] = useState(false);

  const reset = () => {
    setFileName(""); setParsedRows([]); setRowErrors([]); setResult(null);
    setUndoing(false); setUndone(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleClose = (v) => {
    if (!v) { reset(); }
    onOpenChange(v);
  };

  const downloadTemplate = () => {
    if (!preset) return;
    const columns = [...preset.requiredColumns, ...preset.optionalColumns];
    const csv = Papa.unparse({
      fields: columns,
      data: preset.templateSample.map(r => columns.map(c => r[c] ?? "")),
    });
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = preset.templateName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success("Template downloaded");
  };

  const processFile = useCallback((file) => {
    if (!preset) return;
    setResult(null);
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".csv")) {
      toast.error("Please upload a .csv file (XLSX not yet supported — export to CSV first).");
      return;
    }
    setFileName(file.name);
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: h => h.trim().toLowerCase().replace(/\s+/g, "_"),
      complete: (res) => {
        const rows = res.data || [];
        // Validate
        const errs = [];
        rows.forEach((r, i) => {
          for (const col of preset.requiredColumns) {
            if (!r[col] || String(r[col]).trim() === "") {
              errs.push({ row: i + 2, error: `Missing ${col}` });
              break;
            }
          }
        });
        setParsedRows(rows);
        setRowErrors(errs);
        if (errs.length) {
          toast.error(`Found ${errs.length} validation error${errs.length > 1 ? "s" : ""} — fix or skip those rows`);
        } else {
          toast.success(`Parsed ${rows.length} row${rows.length > 1 ? "s" : ""}`);
        }
      },
      error: () => toast.error("Couldn't parse CSV — make sure it's valid"),
    });
  }, [preset]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  };

  const submit = async () => {
    if (!preset) return;
    const validRows = parsedRows.filter((_, i) => !rowErrors.some(e => e.row === i + 2));
    if (!validRows.length) {
      toast.error("No valid rows to import");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post(preset.endpoint, { rows: validRows });
      setResult(res.data);
      const { created, updated, skipped, batch_id } = res.data;
      const summary = `Imported: ${created} new, ${updated} updated${skipped ? `, ${skipped} skipped` : ""}`;
      // For product imports, surface a one-tap "Undo" inside the success
      // toast itself with a 30s window so a misclick can be reverted
      // without re-opening the dialog. Falls back to a plain toast for
      // non-product imports.
      if (type === "products" && batch_id) {
        toast.success(summary, {
          duration: 30000,
          action: {
            label: "Undo",
            onClick: async () => {
              try {
                const r = await api.post(`/products/import-batches/${batch_id}/undo`);
                const { deleted = 0, restored = 0 } = r.data || {};
                toast.success(`Import reverted — ${deleted} deleted, ${restored} restored`);
                setUndone(true);
                onDone?.();
              } catch (err) {
                toast.error(err.response?.data?.detail || "Couldn't undo import");
              }
            },
          },
        });
      } else {
        toast.success(summary);
      }
      onDone?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    }
    setSubmitting(false);
  };

  const undoImport = async () => {
    if (!result?.batch_id) return;
    if (!window.confirm("Undo this import? Newly added products will be deleted and any products it overwrote will be restored to their previous values.")) return;
    setUndoing(true);
    try {
      const res = await api.post(`/products/import-batches/${result.batch_id}/undo`);
      const { deleted = 0, restored = 0 } = res.data || {};
      toast.success(`Import reverted — ${deleted} deleted, ${restored} restored`);
      setUndone(true);
      onDone?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't undo import");
    }
    setUndoing(false);
  };

  if (!preset) return null;

  const columns = [...preset.requiredColumns, ...preset.optionalColumns];
  const validCount = parsedRows.length - rowErrors.length;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" data-testid={`bulk-import-dialog-${type}`}>
        <DialogHeader>
          <DialogTitle className="font-['Outfit'] text-xl tracking-tight">{preset.title}</DialogTitle>
          <p className="text-xs text-slate-500 font-['Manrope'] mt-0.5">{preset.description}</p>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto -mx-6 px-6 py-2 space-y-4">
          {/* Step 1: Download template */}
          <div className="bg-slate-50 border border-slate-100 rounded-xl p-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-white border border-slate-200 flex items-center justify-center">
                <FileSpreadsheet className="w-4 h-4 text-slate-500" />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-900 font-['Outfit']">Need a template?</p>
                <p className="text-xs text-slate-500 font-['Manrope']">Download a pre-formatted CSV with sample rows.</p>
              </div>
            </div>
            <Button onClick={downloadTemplate} data-testid="download-template-btn"
              variant="outline" size="sm" className="border-slate-200 rounded-full h-9 px-4 text-xs">
              <Download className="w-3.5 h-3.5 mr-1.5" />Download Template
            </Button>
          </div>

          {/* Step 2: Upload area */}
          {!result && (
            <div
              onDragEnter={() => setDragOver(true)}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              data-testid="upload-dropzone"
              className={`border-2 border-dashed rounded-2xl p-8 text-center transition-all ${
                dragOver ? "border-rose-400 bg-rose-50/40" : "border-slate-200 bg-slate-50/30 hover:border-slate-300"
              }`}
            >
              <input ref={fileRef} type="file" accept=".csv" className="hidden" data-testid="upload-file-input"
                onChange={(e) => processFile(e.target.files?.[0])} />
              <div className="flex flex-col items-center">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-rose-500 via-violet-500 to-teal-500 flex items-center justify-center mb-4">
                  <Upload className="w-6 h-6 text-white" strokeWidth={1.8} />
                </div>
                <p className="text-sm font-semibold text-slate-900 font-['Outfit']">
                  {fileName ? fileName : "Drop your CSV here or click to browse"}
                </p>
                <p className="text-xs text-slate-400 font-['Manrope'] mt-1">CSV up to 5 MB</p>
                <Button onClick={() => fileRef.current?.click()} size="sm" className="mt-4 rounded-full h-9 px-5 text-xs" data-testid="choose-file-btn">
                  {fileName ? "Choose a different file" : "Choose file"}
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Required columns */}
          {!result && (
            <div className="bg-white border border-slate-100 rounded-xl p-4">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2 font-['Manrope']">Columns</p>
              <div className="flex flex-wrap gap-1.5">
                {preset.requiredColumns.map(c => (
                  <span key={c} className="text-[11px] font-semibold px-2 py-1 rounded-md bg-rose-50 text-rose-700 font-mono">{c}*</span>
                ))}
                {preset.optionalColumns.map(c => (
                  <span key={c} className="text-[11px] font-semibold px-2 py-1 rounded-md bg-slate-50 text-slate-600 font-mono">{c}</span>
                ))}
              </div>
              <p className="text-[11px] text-slate-400 mt-2 font-['Manrope']">* required. Column names are case-insensitive; spaces become underscores.</p>
            </div>
          )}

          {/* Preview */}
          {!result && parsedRows.length > 0 && (
            <div className="bg-white border border-slate-100 rounded-xl overflow-hidden" data-testid="preview-section">
              <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between bg-slate-50/60">
                <p className="text-xs font-semibold text-slate-900 font-['Manrope']">
                  Preview — {parsedRows.length} row{parsedRows.length > 1 ? "s" : ""}
                  {rowErrors.length > 0 && (
                    <span className="ml-2 text-rose-600">({rowErrors.length} with errors)</span>
                  )}
                </p>
                <p className="text-[11px] text-slate-500 font-['Manrope']">Showing first 5</p>
              </div>
              <div className="overflow-x-auto max-h-[260px]">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50/80 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold text-slate-500 text-[10px] uppercase tracking-widest">Row</th>
                      {columns.slice(0, 6).map(c => (
                        <th key={c} className="px-3 py-2 text-left font-semibold text-slate-500 text-[10px] uppercase tracking-widest">{c}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {parsedRows.slice(0, 5).map((r, i) => {
                      const err = rowErrors.find(e => e.row === i + 2);
                      return (
                        <tr key={i} className={err ? "bg-rose-50/50" : ""}>
                          <td className="px-3 py-2 text-slate-400 tabular-nums">{i + 2}</td>
                          {columns.slice(0, 6).map(c => (
                            <td key={c} className="px-3 py-2 text-slate-700 truncate max-w-[150px]">{r[c] || "—"}</td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {rowErrors.length > 0 && (
                <div className="border-t border-rose-100 bg-rose-50/40 px-4 py-3">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-rose-500 mt-0.5 flex-shrink-0" />
                    <div className="text-[11px] text-rose-700 font-['Manrope'] space-y-0.5">
                      <p className="font-semibold">{rowErrors.length} row{rowErrors.length > 1 ? "s have" : " has"} errors and will be skipped:</p>
                      {rowErrors.slice(0, 3).map((e, i) => (
                        <p key={i}>Row {e.row}: {e.error}</p>
                      ))}
                      {rowErrors.length > 3 && <p className="italic">…and {rowErrors.length - 3} more</p>}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Result */}
          {result && (
            <div className="bg-white border border-emerald-200 rounded-2xl p-6 text-center" data-testid="import-result">
              <div className="w-14 h-14 rounded-2xl bg-emerald-500 flex items-center justify-center mx-auto mb-4">
                <Check className="w-7 h-7 text-white" strokeWidth={2.5} />
              </div>
              <h3 className="font-['Outfit'] text-xl font-semibold text-slate-900">Import complete</h3>
              <div className="grid grid-cols-3 gap-3 mt-5 max-w-sm mx-auto">
                <div className="bg-emerald-50 rounded-xl p-3">
                  <p className="font-['Outfit'] text-2xl font-semibold text-emerald-700 tabular-nums">{result.created}</p>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-600 font-['Manrope']">New</p>
                </div>
                <div className="bg-amber-50 rounded-xl p-3">
                  <p className="font-['Outfit'] text-2xl font-semibold text-amber-700 tabular-nums">{result.updated}</p>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-amber-600 font-['Manrope']">Updated</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-3">
                  <p className="font-['Outfit'] text-2xl font-semibold text-slate-500 tabular-nums">{result.skipped}</p>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 font-['Manrope']">Skipped</p>
                </div>
              </div>
              {result.errors?.length > 0 && (
                <div className="mt-4 text-left bg-rose-50 border border-rose-100 rounded-xl p-3 max-h-[120px] overflow-y-auto">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-rose-600 font-['Manrope'] mb-1">Errors</p>
                  {result.errors.slice(0, 5).map((e, i) => (
                    <p key={i} className="text-xs text-rose-700 font-['Manrope']">Row {e.row}: {e.error}</p>
                  ))}
                </div>
              )}
              {type === "products" && result.batch_id && !undone && (
                <div className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-center gap-2">
                  <p className="text-[11px] text-slate-500 font-['Manrope']">Wrong file? Roll the whole import back —</p>
                  <Button onClick={undoImport} disabled={undoing}
                    data-testid="undo-import-btn"
                    variant="outline" size="sm"
                    className="border-rose-200 text-rose-600 hover:bg-rose-50 rounded-full h-8 px-3 text-xs">
                    {undoing ? (
                      <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Undoing…</>
                    ) : (
                      <><Undo2 className="w-3 h-3 mr-1" />Undo this import</>
                    )}
                  </Button>
                </div>
              )}
              {undone && (
                <p className="mt-4 text-xs text-rose-600 font-semibold font-['Manrope']" data-testid="undo-done-msg">
                  Import reverted.
                </p>
              )}
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100">
          {result ? (
            <Button onClick={() => handleClose(false)} className="rounded-full h-10 px-5 text-sm bg-slate-900 text-white hover:bg-slate-800" data-testid="import-close-btn">Done</Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => handleClose(false)} className="border-slate-200 rounded-full h-10 px-5 text-sm">
                <X className="w-3.5 h-3.5 mr-1" />Cancel
              </Button>
              <Button onClick={submit} disabled={!parsedRows.length || submitting || validCount === 0}
                data-testid="import-submit-btn"
                className="rounded-full h-10 px-5 text-sm bg-gradient-to-r from-rose-500 to-violet-500 hover:from-rose-600 hover:to-violet-600 text-white border-0 disabled:opacity-40">
                {submitting ? (
                  <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Importing…</>
                ) : (
                  <>Import {validCount > 0 ? `${validCount} row${validCount > 1 ? "s" : ""}` : ""}</>
                )}
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
